import { useCallback, useEffect, useRef, useState } from "react";
import { PaginationState } from "@tanstack/react-table";
import { useDebounce } from "@react-hook/debounce";
import usePrevious from "utils/usePrevious";

export function usePaginationReset<T>(
  value: T,
  setPageIndex: (idx: number) => void,
  currentPageIndex: number | undefined,
) {
  const [deferredValue, setDeferredValue] = useState(value);

  const prevValue = usePrevious(value);

  const isPageResetRequired =
    typeof currentPageIndex === "number" && currentPageIndex > 0;

  useEffect(() => {
    if (value !== prevValue) {
      if (isPageResetRequired) {
        setPageIndex(0);
      }
      setDeferredValue(value);
    }
  }, [value, setPageIndex, prevValue, isPageResetRequired]);

  return isPageResetRequired && deferredValue !== value;
}

export default function usePagination(
  defaultPagination?: Partial<PaginationState>,
) {
  const [pagination, onPaginationChange] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
    ...defaultPagination,
  });

  const { pageIndex, pageSize } = pagination;

  const [debouncedPageIndex, setDebouncedPageIndex, setPageIndexImmediately] =
    useDebounce(pageIndex, 200, false);

  useEffect(() => {
    setDebouncedPageIndex(pageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex]);

  const setPageIndex = useCallback(
    (newPageIndex: number) => {
      setPageIndexImmediately(newPageIndex);
      onPaginationChange((existing) => ({
        ...existing,
        pageIndex: newPageIndex,
      }));
    },
    [setPageIndexImmediately],
  );

  // Track the last known totalCount using a ref
  const prevTotalCountRef = useRef<number | null | undefined>(undefined);

  // Modified getPageCount that validates pagination
  const getPageCount = useCallback(
    (totalCount: number | null | undefined) => {
      const pageCount = (totalCount && Math.ceil(totalCount / pageSize)) ?? -1;

      // Check if we need to reset pagination
      if (
        totalCount != null &&
        prevTotalCountRef.current !== undefined && // Skip first render
        prevTotalCountRef.current !== totalCount && // Total count changed
        pageIndex > 0 && // Not on first page
        pageIndex >= Math.max(pageCount, 0) // Current page is out of bounds (handles pageCount 0 or -1)
      ) {
        // Go to last page
        setPageIndex(pageCount - 1);
      }

      // Update the ref for next comparison
      prevTotalCountRef.current = totalCount;

      return pageCount;
    },
    [pageSize, pageIndex, setPageIndex],
  );

  return {
    pagination,
    onPaginationChange,
    limitOffset: { limit: pageSize, offset: pageSize * debouncedPageIndex },
    getPageCount,
    setPageIndex,
  };
}
